import { ReactNode } from 'react';
import { useSubscription, PlanType } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

interface SubscriptionGateProps {
  children?: ReactNode;
  requiredPlan: PlanType;
  feature?: string;
  description?: string;
  fallback?: ReactNode;
}

const PLAN_NAMES: Record<PlanType, string> = {
  free: 'Free',
  pro: 'Pro',
  premium: 'Premium',
};

export const SubscriptionGate = ({ 
  children, 
  requiredPlan, 
  feature,
  description,
  fallback 
}: SubscriptionGateProps) => {
  const { subscription, loading } = useSubscription();

  if (loading) {
    return <div className="animate-pulse h-20 bg-muted rounded-lg" />;
  }

  const planHierarchy: Record<PlanType, number> = {
    free: 0,
    pro: 1,
    premium: 2,
  };

  const currentPlanLevel = subscription ? planHierarchy[subscription.plan_type] : 0;
  const requiredPlanLevel = planHierarchy[requiredPlan];

  if (currentPlanLevel >= requiredPlanLevel) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  return (
    <Card className="p-8 text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
        <Lock className="h-8 w-8 text-primary" />
      </div>
      <h3 className="mb-2 text-xl font-semibold">
        {feature ? `${feature} is a ${PLAN_NAMES[requiredPlan]} Feature` : `Upgrade to ${PLAN_NAMES[requiredPlan]}`}
      </h3>
      <p className="mb-6 text-muted-foreground">
        {description || 'Upgrade your plan to access this feature and unlock more capabilities.'}
      </p>
      <Link to="/pricing">
        <Button size="lg">
          View Plans
        </Button>
      </Link>
    </Card>
  );
};
